import {lazy} from 'react';

import {
  createRoute,
  redirectRoute,
  layoutComponent,
  withRouter,
  type AnyRoute
} from 'client/routeHelpers';

const CreateAccounts = lazy(() => import('client/components/CreateAccounts/CreateAccounts.react'));
const SchoolAdmin = lazy(() => import('client/SchoolAdministration/SchoolAdministration'));
const SchoolList = lazy(() => import('client/SchoolAdministration/SchoolList/SchoolList.react'));
const School = lazy(() => import('client/SchoolAdministration/School/School'));
const SchoolAdminDashboard = lazy(
  () => import('client/SchoolAdministration/School/Dashboard/Dashboard.react')
);
const SchoolAdminManageStaff = lazy(
  () => import('client/SchoolAdministration/School/ManageStaff/ManageStaff')
);
const SchoolAccounts = lazy(
  () => import('client/SchoolAdministration/School/SchoolAccounts/SchoolAccounts.react')
);
const SchoolAdminPendingApprovalStaff = lazy(
  () => import('client/SchoolAdministration/School/ManageStaff/legacy/PendingApproval')
);
const SchoolAdminPendingInvitationsStaff = lazy(
  () => import('client/SchoolAdministration/School/ManageStaff/legacy/PendingInvitations')
);
const SchoolAdminApprovedStaff = lazy(
  () => import('client/SchoolAdministration/School/ManageStaff/legacy/Approved')
);
const SchoolAdminRejectedStaff = lazy(
  () => import('client/SchoolAdministration/School/ManageStaff/legacy/Rejected')
);
const SchoolAdminUpdateTeachers = lazy(
  () =>
    import('client/SchoolAdministration/School/ManageStaff/legacy/UpdateTeachers/UpdateTeachers')
);
const SchoolAdminAllStaff = lazy(
  () => import('client/SchoolAdministration/School/ManageStaff/AllStaff/AllStaff')
);
const SchoolAdminUsageReports = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/UsageReports')
);
const SchoolAdminTeachersReport = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/Reports/TeachersReport.react')
);
const SchoolAdminStudentsReport = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/Reports/StudentsReport.react')
);
const SchoolAdminClassesReport = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/Reports/ClassesReport.react')
);
const SchoolAdminSubjectsReport = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/Reports/SubjectsReport.react')
);

/**
 * `/school-admin` — school list at the index, and a `$schoolId` layout that
 * fans out into dashboard, manage-staff, usage-reports, and account tools.
 * 1:1 replacement of the RR3 route module (same paths, redirects, indexes).
 */
export function makeSchoolAdminRoute(parentRoute: AnyRoute): AnyRoute {
  const schoolAdminRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: 'school-admin',
    component: layoutComponent(SchoolAdmin)
  });

  const schoolAdminIndex = createRoute({
    getParentRoute: () => schoolAdminRoute,
    path: '/',
    component: withRouter(SchoolList)
  });

  // --- $schoolId layout ---
  const schoolIdRoute = createRoute({
    getParentRoute: () => schoolAdminRoute,
    path: '$schoolId',
    component: layoutComponent(School)
  });

  const schoolIdIndexRedirect = redirectRoute({
    getParentRoute: () => schoolIdRoute,
    path: '/',
    to: '/school-admin/$schoolId/dashboard'
  });
  const dashboardRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'dashboard',
    component: withRouter(SchoolAdminDashboard)
  });
  const classesRedirect = redirectRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'classes',
    to: '/school-admin/$schoolId/usage-reports/classes'
  });
  const teachersRedirect = redirectRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'teachers',
    to: '/school-admin/$schoolId/usage-reports/teachers'
  });
  const licensesRedirect = redirectRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'licenses',
    to: '/school-admin/$schoolId/manage-staff'
  });

  // --- manage-staff layout ---
  const manageStaffRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'manage-staff',
    component: layoutComponent(SchoolAdminManageStaff)
  });
  const manageStaffIndexRedirect = redirectRoute({
    getParentRoute: () => manageStaffRoute,
    path: '/',
    to: '/school-admin/$schoolId/manage-staff/pending-approval'
  });
  const pendingApprovalRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'pending-approval',
    component: withRouter(SchoolAdminPendingApprovalStaff)
  });
  const pendingInvitationsRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'pending-invitations',
    component: withRouter(SchoolAdminPendingInvitationsStaff)
  });
  const approvedRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'approved',
    component: withRouter(SchoolAdminApprovedStaff)
  });
  const rejectedRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'rejected',
    component: withRouter(SchoolAdminRejectedStaff)
  });
  const allStaffRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'all-staff',
    component: withRouter(SchoolAdminAllStaff)
  });
  const reviewNeededRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'review-needed',
    component: withRouter(SchoolAdminPendingApprovalStaff)
  });
  const invitesRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'invites',
    component: withRouter(SchoolAdminPendingInvitationsStaff)
  });
  manageStaffRoute.addChildren([
    manageStaffIndexRedirect,
    pendingApprovalRoute,
    pendingInvitationsRoute,
    approvedRoute,
    rejectedRoute,
    allStaffRoute,
    reviewNeededRoute,
    invitesRoute
  ]);

  const updateTeachersRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'update-teachers',
    component: withRouter(SchoolAdminUpdateTeachers)
  });

  // --- usage-reports layout ---
  const usageReportsRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'usage-reports',
    component: layoutComponent(SchoolAdminUsageReports)
  });
  const usageReportsIndexRedirect = redirectRoute({
    getParentRoute: () => usageReportsRoute,
    path: '/',
    to: '/school-admin/$schoolId/usage-reports/teachers'
  });
  const usageTeachersRoute = createRoute({
    getParentRoute: () => usageReportsRoute,
    path: 'teachers',
    component: withRouter(SchoolAdminTeachersReport)
  });
  const usageStudentsRoute = createRoute({
    getParentRoute: () => usageReportsRoute,
    path: 'students',
    component: withRouter(SchoolAdminStudentsReport)
  });
  const usageClassesRoute = createRoute({
    getParentRoute: () => usageReportsRoute,
    path: 'classes',
    component: withRouter(SchoolAdminClassesReport)
  });
  const usageSubjectsRoute = createRoute({
    getParentRoute: () => usageReportsRoute,
    path: 'subjects',
    component: withRouter(SchoolAdminSubjectsReport)
  });
  usageReportsRoute.addChildren([
    usageReportsIndexRedirect,
    usageTeachersRoute,
    usageStudentsRoute,
    usageClassesRoute,
    usageSubjectsRoute
  ]);

  const schoolAccountsRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'school-accounts',
    component: withRouter(SchoolAccounts)
  });
  const createAccountsRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'create-accounts',
    component: withRouter(CreateAccounts)
  });

  schoolIdRoute.addChildren([
    schoolIdIndexRedirect,
    dashboardRoute,
    classesRedirect,
    teachersRedirect,
    licensesRedirect,
    manageStaffRoute,
    updateTeachersRoute,
    usageReportsRoute,
    schoolAccountsRoute,
    createAccountsRoute
  ]);

  return schoolAdminRoute.addChildren([schoolAdminIndex, schoolIdRoute]);
}
